/**
 * Tâche 1281 (Eric 2026-06-29) — Journal des injections.
 *
 * Les ajouts étaient tracés mais invisibles : impossible de savoir qu'un lot
 * de trente passages était parti la veille, ni de le défaire sans ouvrir
 * trente fenêtres. Ici, un lot par ligne (auteur, minute, sens, nombre de
 * passages) et trois mailles de retrait : le lot, la vidéo, l'auteur.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE as API } from "../../lib/apiBase";
import { confirmDialog } from "../../lib/uiDialogs";
import { useLang } from "../../contexts/LanguageContext";

// Tâche 1410 (Eric 2026-09-30) — Ces libellés décrivent l'action, pas une
// donnée saisie : ils portent donc leurs deux langues sur la même ligne.
// Tâche 1410 (Eric 2026-10-01) — La table était INCOMPLÈTE : `retrait`,
// `merge`, `purge` et `segment` sont écrits par le serveur mais n'y figuraient
// pas, et le repli `|| b.action` affichait alors la CLÉ FRANÇAISE brute au
// milieu d'une ligne anglaise (« 1 passage(s) · -498.7 s · retrait · by … »).
// Même défaut que la source brute de 1421 : un repli qui a l'air d'un libellé.
const ACTION_LABELS = {
  ajout_lot_fireflies: { fr: "Fireflies → fusion", en: "Fireflies → merge" },
  ajout_lot_whisper: { fr: "Whisper → fusion", en: "Whisper → merge" },
  ajout: { fr: "Ajout manuel", en: "Manual addition" },
  correction: { fr: "Correction manuelle", en: "Manual correction" },
  retrait: { fr: "Retrait", en: "Removal" },
  merge: { fr: "Fusion", en: "Merge" },
  purge: { fr: "Purge", en: "Purge" },
  segment: { fr: "Segment", en: "Segment" },
};

export const GapInjectionsJournal = ({ videoId = null }) => {
  const { lang, tr } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/api/videos/coverage-gaps/injections${videoId ? `?video_id=${videoId}` : ""}`,
        { withCredentials: true },
      );
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Journal indisponible", "Journal unavailable"));
    } finally {
      setLoading(false);
    }
  }, [videoId, tr]);

  useEffect(() => { load(); }, [load]);

  const remove = async (payload, question) => {
    const ok = await confirmDialog(question, { tone: "warning", confirmLabel: tr("Retirer", "Remove") });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await axios.post(
        `${API}/api/videos/coverage-gaps/injections/remove`,
        payload,
        { withCredentials: true, timeout: 120000 },
      );
      toast.success(tr(
        `${r.removed} passage(s) retiré(s) de ${r.videos} vidéo(s)`,
        `${r.removed} passage(s) removed from ${r.videos} video(s)`,
      ));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Retrait impossible", "Could not remove"));
    } finally {
      setBusy(false);
    }
  };

  const batches = data?.batches || [];

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="gap-injections-journal">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">{tr("Journal des injections", "Injections journal")}</h2>
          <p className="text-xs text-zinc-500">
            {tr(
              "Qui a injecté quoi, et quand. Un lot par ligne — retirable en un geste.",
              "Who injected what, and when. One batch per line — removable in one move.",
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading || busy}
          className="text-xs px-2 py-1 rounded border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="gap-injections-refresh"
        >
          {loading ? "⏳" : tr("↻ Actualiser", "↻ Refresh")}
        </button>
      </div>

      {(data?.authors || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
          <span className="text-zinc-500">{tr("Retirer tout ce qu'a injecté", "Remove everything injected by")} :</span>
          {data.authors.map((a) => (
            <button
              key={a}
              type="button"
              disabled={busy}
              onClick={() => remove({ author: a }, tr(
                `Retirer TOUS les passages injectés par ${a} ? Le texte issu de la fusion n'est pas touché.`,
                `Remove EVERY passage injected by ${a}? The text produced by the merge is left untouched.`,
              ))}
              className="px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 disabled:opacity-50"
              data-testid={`gap-injections-remove-author-${a}`}
            >
              {a} ✕
            </button>
          ))}
        </div>
      )}

      {batches.length === 0 ? (
        <p className="text-sm text-zinc-500 italic" data-testid="gap-injections-empty">
          {loading ? tr("Chargement…", "Loading…") : tr("Aucun passage injecté pour le moment.", "No passage injected yet.")}
        </p>
      ) : (
        <ul className="divide-y divide-zinc-100" data-testid="gap-injections-list">
          {batches.map((b) => (
            <li key={b.batch_key} className="py-2 flex items-start gap-3 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-zinc-900 truncate">{b.video_title}</p>
                <p className="text-xs text-zinc-600">
                  <b>{b.count}</b> {tr("passage(s)", b.count === 1 ? "passage" : "passages")} · {b.seconds} s ·{" "}
                  {(ACTION_LABELS[b.action] && (lang === "en" ? ACTION_LABELS[b.action].en : ACTION_LABELS[b.action].fr)) || b.action}
                  {" · "}{tr("par", "by")} <b>{b.by || "?"}</b> {tr("le", "on")}{" "}
                  {String(b.minute || "").replace("T", " ")}
                </p>
                {b.samples?.length > 0 && (
                  <p className="text-[11px] text-zinc-400 italic truncate">« {b.samples[0]} »</p>
                )}
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => remove({ batch_key: b.batch_key }, tr(
                    `Retirer les ${b.count} passage(s) injectés le ${String(b.minute || "").replace("T", " ")} ?`,
                    `Remove the ${b.count} passage(s) injected on ${String(b.minute || "").replace("T", " ")}?`,
                  ))}
                  className="text-[11px] px-2 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 disabled:opacity-50"
                  data-testid={`gap-injections-remove-batch-${b.video_id}`}
                >
                  {tr("Retirer ce lot", "Remove this batch")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => remove({ video_id: b.video_id }, tr(
                    `Retirer TOUS les passages injectés dans « ${b.video_title} » ?`,
                    `Remove EVERY passage injected into "${b.video_title}"?`,
                  ))}
                  className="text-[11px] px-2 py-0.5 rounded border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                  data-testid={`gap-injections-remove-video-${b.video_id}`}
                >
                  {tr("Toute la vidéo", "The whole video")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {batches.length > 0 && (
        <p className="text-[11px] text-zinc-400 mt-2">
          {tr(
            `${data.total_passages} passage(s) injecté(s) au total, en ${data.total_batches} lot(s).`,
            `${data.total_passages} passage${data.total_passages === 1 ? "" : "s"} injected in total, in ${data.total_batches} batch${data.total_batches === 1 ? "" : "es"}.`,
          )}
        </p>
      )}
    </section>
  );
};

export default GapInjectionsJournal;
